export const MarketingTab = async (container) => {
    // [Phase 4 Refactor] Marketing Logic via Service
    try {
        // Dynamic Imports
        const { AdminMarketingService } = await import("../../../services/admin-marketing.service.js");
        const { SeriesPickerModal } = await import("../../../components/common/SeriesPickerModal.js");
        const { CampaignCreationModal } = await import("../../../components/modals/CampaignCreationModal.js");

        // 1. Fetch Events
        let events = await AdminMarketingService.fetchEvents();

        // 2. Render Functions
        const renderEventList = () => {
            const listContainer = container.querySelector('#event-list');
            if (!listContainer) return;

            if (events.length === 0) {
                listContainer.innerHTML = `
                    <div class="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        <span class="material-symbols-outlined text-4xl text-gray-300 mb-2">event_busy</span>
                        <p class="text-gray-400 text-sm">등록된 이벤트가 없습니다.</p>
                        <button id="btn-create-first" class="mt-4 text-indigo-600 font-bold text-sm hover:underline">첫 이벤트 만들기</button>
                    </div>
                `;
                const btnFirst = listContainer.querySelector('#btn-create-first');
                if (btnFirst) btnFirst.onclick = () => {
                    const createBtn = container.querySelector('#btn-create-event');
                    if (createBtn) createBtn.click();
                }
                return;
            }

            listContainer.innerHTML = events.map(event => {
                // Category display
                const CAT_DISPLAY = {
                    reading_reward: { icon: 'auto_stories', color: 'green', label: '감상' },
                    attendance: { icon: 'calendar_month', color: 'blue', label: '출석' },
                    share: { icon: 'share', color: 'pink', label: '공유' },
                    referral: { icon: 'group_add', color: 'orange', label: '초대' },
                    welcome: { icon: 'waving_hand', color: 'purple', label: '환영' }
                };
                const cat = CAT_DISPLAY[event.boostCategory] || CAT_DISPLAY['reading_reward'];
                const target = event.seriesTitle || 'Global (전체)';

                return `
                <div class="bg-white border border-gray-100 rounded-xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-all group">
                    <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-lg bg-${cat.color}-100 flex items-center justify-center text-${cat.color}-600 font-bold text-xl">
                            ${event.multiplier}x
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-${cat.color}-100 text-${cat.color}-600 uppercase flex items-center gap-0.5">
                                    <span class="material-symbols-outlined text-[10px]">${cat.icon}</span> ${cat.label}
                                </span>
                                <h4 class="font-bold text-gray-900">${event.title}</h4>
                            </div>
                            <p class="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                <span class="material-symbols-outlined text-[14px]">calendar_month</span>
                                ${event.startDate && event.startDate.toDate ? event.startDate.toDate().toLocaleString() : new Date(event.startDate).toLocaleString()} ~ 
                                ${event.endDate && event.endDate.toDate ? event.endDate.toDate().toLocaleString() : new Date(event.endDate).toLocaleString()}
                                <span class="w-1 h-1 bg-gray-300 rounded-full"></span>
                                Target: ${target}
                            </p>
                        </div>
                    </div>
                    
                    <div class="flex items-center gap-2">
                        <!-- Toggle Switch -->
                        <button class="btn-toggle-event relative w-10 h-6 rounded-full transition-colors ${event.isActive ? 'bg-green-500' : 'bg-gray-300'}" 
                            data-id="${event.id}" data-active="${event.isActive}">
                            <div class="absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${event.isActive ? 'translate-x-4' : ''}"></div>
                        </button>
                        
                        <!-- Delete Button -->
                        <button class="btn-delete-event p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors" data-id="${event.id}">
                            <span class="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                </div>`;
            }).join('');

            // Bind Actions
            listContainer.querySelectorAll('.btn-toggle-event').forEach(btn => {
                btn.onclick = async (e) => {
                    const id = btn.dataset.id;
                    const currentStatus = btn.dataset.active === 'true';
                    await AdminMarketingService.toggleEventStatus(id, currentStatus);
                    // Optimistic Update
                    const ev = events.find(e => e.id === id);
                    if (ev) ev.isActive = !currentStatus;
                    renderEventList();
                };
            });

            listContainer.querySelectorAll('.btn-delete-event').forEach(btn => {
                btn.onclick = async (e) => {
                    // [FIX] Use Custom Modal Correctly (Promise-based)
                    const confirmed = await window.Modal.confirm('이벤트를 삭제하시겠습니까? \n삭제된 이벤트는 복구할 수 없습니다.', '삭제 확인');

                    if (confirmed) {
                        try {
                            await AdminMarketingService.deleteEvent(btn.dataset.id);
                            // Optimistic UI Update or Refetch
                            events = events.filter(ev => ev.id !== btn.dataset.id);
                            renderEventList();
                        } catch (err) {
                            console.error(err);
                            window.Modal.alert("삭제 중 오류가 발생했습니다.");
                        }
                    }
                };
            });
        };

        // --- 3. Initial Render ---
        container.innerHTML = `
            <div class="animate-fade-in text-left">
                <div class="flex justify-between items-end mb-6">
                    <div>
                        <h2 class="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                            <span class="material-symbols-outlined text-yellow-500 text-3xl">campaign</span> MARKETING OPS
                        </h2>
                        <p class="text-xs text-gray-500 font-mono mt-1">GOLDEN TIME & PROMOTIONS</p>
                    </div>
                </div>

                <!-- Event Stats -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                     <div class="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-5 text-white shadow-lg">
                        <p class="text-xs font-bold text-yellow-100 uppercase mb-1">Active Events</p>
                        <h3 class="text-3xl font-black">${events.filter(e => e.isActive).length}</h3>
                    </div>
                    <div class="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                        <p class="text-xs font-bold text-gray-500 uppercase mb-1">Total ROI</p>
                        <h3 class="text-3xl font-black text-gray-900">+24%</h3>
                    </div>
                    <button id="btn-create-event" class="bg-gray-900 hover:bg-black text-white rounded-xl p-5 shadow-lg flex flex-col justify-center items-center gap-2 transition-transform active:scale-95">
                        <span class="material-symbols-outlined text-3xl">add_circle</span>
                        <span class="font-bold text-sm">New Campaign</span>
                    </button>
                </div>

                <!-- List -->
                <h3 class="text-sm font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
                    <span class="material-symbols-outlined">event_note</span> Campaign History
                </h3>
                <div id="event-list" class="space-y-3 pb-20"></div>
            </div>
        `;

        renderEventList();

        // Bind Create Button (Complex Logic Logic preserved using Swal)
        const btnCreate = container.querySelector('#btn-create-event');
        if (btnCreate) {
            btnCreate.onclick = async () => {
                new CampaignCreationModal(async () => {
                    // Refresh List on Success
                    events = await AdminMarketingService.fetchEvents();
                    renderEventList();
                }).render();
            };
        }

    } catch (e) {
        console.error(e);
        container.innerHTML = `<p class="text-red-500 p-6">MarketingTab Error: ${e.message}</p>`;
    }
};
